import { memo } from "react";
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { useEffect } from "react";
import { Element_Base, ElementType } from "~/common/typescript/models/Element";
import { ElementAPIs } from "~/common/typescript/apis/ElementAPIs";
import { elementsAtom, selectedElementAtom, elementDisplayedBoundsAtom, showElementLabelsAtom } from "~/components/Map/Element/ElementStore";
import { ElementMapUtilOptimized } from "./ElementMapUtilOptimized";
import { ElementLabelOverlay } from "./ElementLabelOverlay";
import { showElementDetailPanelAtom } from "../stores/mapStore";
import { ElementItemUtil } from "../panels/LegendPanel/ListTab/ElementGroup/ElementItemUtil";
import MapUtils from "../utils/MapUtils";
import { elementType_Atom } from "~/layouts/components/Header/HeaderCenter/Element/store";
import { showSamPanelAtom, showChatSamPanelAtom } from "../stores/mapStore";
import { isMapLoadedDoneAtom } from "../stores/mapStore";
import { LngLatBounds } from "maplibre-gl";
import * as Turf from '@turf/turf';
import { BasemapMapUtil } from "~/layouts/components/Header/components/BasemapSelector/BasemapMapUtil";

const ElementMapController = () => {
  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const [elements, setElements] = useAtom<Element_Base[]>(elementsAtom);
  const [showElementDetailPanel, setShowElementDetailPanel] = useAtom(showElementDetailPanelAtom);
  const [selectedElement, setSelectedElement] = useAtom(selectedElementAtom);
  const [elementMode, setElementMode] = useAtom(elementType_Atom);
  const [showSamPanel, setShowSamPanel] = useAtom(showSamPanelAtom);
  const [showChatSamPanel, setShowChatSamPanel] = useAtom(showChatSamPanelAtom);
  const [isMapLoadedDone, setMapLoadedDone] = useAtom(isMapLoadedDoneAtom);
  const setElementDisplayedBounds = useSetAtom(elementDisplayedBoundsAtom);
  const showElementLabels = useAtomValue(showElementLabelsAtom);

  const fetchData = async () => {
    
    const elements = await ElementAPIs.remote_getPage({
      page: 1,
      pageSize: 50,
      project_id: currentProject?.id!,
    });
    console.log('elements length', elements.results.length);
    setElements(elements.results);
  }

  useEffect(() => {
    if (!currentProject) return;
    if (!isMapLoadedDone) return;

    fetchData();
  }, [isMapLoadedDone, currentProject]);

  useEffect(() => {
    if (!isMapLoadedDone) return;
    if (elementMode == ElementType.SAM) {
      setShowSamPanel(true);
    } else if (elementMode == ElementType.CHAT_SAM) {
      setShowChatSamPanel(true);
    }

    // const onMapClick = (event: maplibregl.MapMouseEvent) => {
    //   // add a point on map just use maplibre point layer
    //   // no need to make a new element
    //   const point = event.lngLat;
    //   if (!point) return;
    //   const sourceId = `element-source-point-sam`;
    //   const layerIdPrefix = `element-layer-point-sam`;

    //   // check layer, source exist remove it first
    //   if (window.map?.getLayer(layerIdPrefix)) {
    //     window.map?.removeLayer(layerIdPrefix);
    //   }
    //   if (window.map?.getSource(sourceId)) {
    //     window.map?.removeSource(sourceId);
    //   }

    //   window.map?.addSource(sourceId, {
    //     type: 'geojson',
    //     data: {
    //       type: 'FeatureCollection',
    //       features: [
    //         {
    //           type: 'Feature',
    //           properties: {},
    //           geometry: {
    //             type: 'Point',
    //             coordinates: [point.lng, point.lat],
    //           },
    //         },
    //       ],
    //     },
    //   });
    //   window.map?.addLayer({
    //     id: `${layerIdPrefix}`,
    //     source: sourceId,
    //     type: 'circle',
    //     paint: {
    //       'circle-radius': 10,
    //       'circle-color': '#ff0000',
    //       'circle-stroke-color': '#fff',
    //       'circle-stroke-width': 2,
    //       'circle-opacity': 1.0,
    //     },
    //   });
    // }

    // if (elementMode == ElementType.SAM) {
    //   window.map?.on('click', onMapClick);
    // }

    // return () => {
    //   window.map?.off('click', onMapClick);
    // }
  }, [isMapLoadedDone, elementMode]);

  // Clear selectedElement if it no longer exists in elements list (i.e., was deleted)
  useEffect(() => {
    if (selectedElement && !elements.find(el => el.uuid === selectedElement.uuid)) {
      setSelectedElement(null);
      setShowElementDetailPanel(false);
      // Clear any existing highlight on the map
      MapUtils.clearHighlight();
    }
  }, [elements, selectedElement, setSelectedElement, setShowElementDetailPanel]);

  useEffect(() => {
    // Optimized: Only update changed elements instead of remove all + add all
    ElementMapUtilOptimized.updateElements(elements);

    // Update element label overlays
    ElementLabelOverlay.updateLabelsVisibility(elements, showElementLabels);

    let combinedElementBounds: LngLatBounds | null = null;
    let elementsWithGeom = 0;
    let elementsWithValidBounds = 0;

    elements.forEach(element => {
      if (element.geom) {
        elementsWithGeom++;

        // Check if geom is a string that needs parsing
        let geomObj = element.geom;
        if (typeof element.geom === 'string') {
          try {
            geomObj = JSON.parse(element.geom);
          } catch (parseError) {
            console.error('ElementMapController - Failed to parse geom string:', element.uuid, parseError);
            return;
          }
        }

        // Check geometry type

        try {
          const bbox = Turf.bbox(geomObj);
          const elementBounds = new LngLatBounds([bbox[0], bbox[1]], [bbox[2], bbox[3]]);
          if (combinedElementBounds) {
            combinedElementBounds.extend(elementBounds);
          } else {
            combinedElementBounds = elementBounds;
          }
          elementsWithValidBounds++;
        } catch (error) {
          console.error("Error calculating bounds for element:", element.uuid, error);
        }
      } else {
      }
    });


    if (combinedElementBounds) {
      const boundsString = JSON.stringify([
        [combinedElementBounds.getWest(), combinedElementBounds.getSouth()],
        [combinedElementBounds.getEast(), combinedElementBounds.getNorth()],
      ]);
      setElementDisplayedBounds(boundsString);
    } else {
      setElementDisplayedBounds(null);
    }

    { // register events with buffer for easier clicking
      if (!window.map) return;

      // Remove existing event listeners first
      if ((window.map as any).elementClickHandler) {
        window.map?.off('click', (window.map as any).elementClickHandler);
      }
      if ((window.map as any).elementDblClickHandler) {
        window.map?.off('dblclick', (window.map as any).elementDblClickHandler);
      }

      // Get all element layer IDs
      const elementLayerIds = elements.flatMap((element) => [
        `element-layer-${element.uuid}-circle`,
        `element-layer-${element.uuid}-line`,
        `element-layer-${element.uuid}-fill`,
        `element-layer-${element.uuid}-outline`
      ]);

      // Single click handler with buffer
      const clickHandler = (e: maplibregl.MapMouseEvent) => {
        // Add buffer for easier clicking on elements
        const bufferRadius = 10; // 20px buffer around click point

        // Filter out layer IDs that don't exist in the map
        const existingLayerIds = elementLayerIds.filter(layerId => {
          return window.map?.getLayer(layerId) !== undefined;
        });

        if (existingLayerIds.length === 0) return;

        const features = window.map?.queryRenderedFeatures([
          [e.point.x - bufferRadius, e.point.y - bufferRadius],
          [e.point.x + bufferRadius, e.point.y + bufferRadius]
        ], { layers: existingLayerIds });

        if (features && features.length > 0) {
          const feature = features[0];
          // Extract element UUID from layer ID
          const layerId = feature.layer.id;
          const uuidMatch = layerId.match(/element-layer-(.+?)-(circle|line|fill|outline)/);
          if (uuidMatch) {
            const elementUuid = uuidMatch[1];
            const element = elements.find(el => el.uuid === elementUuid);
            if (element) {
              ElementItemUtil.highlightElement(element);
              setShowElementDetailPanel(true);
              setSelectedElement(element);
            }
          }
        }
      };

      // Double click handler with buffer
      const dblClickHandler = (e: maplibregl.MapMouseEvent) => {
        // Add buffer for easier double clicking on elements
        const bufferRadius = 10; // 20px buffer around click point

        // Filter out layer IDs that don't exist in the map
        const existingLayerIds = elementLayerIds.filter(layerId => {
          return window.map?.getLayer(layerId) !== undefined;
        });

        if (existingLayerIds.length === 0) return;

        const features = window.map?.queryRenderedFeatures([
          [e.point.x - bufferRadius, e.point.y - bufferRadius],
          [e.point.x + bufferRadius, e.point.y + bufferRadius]
        ], { layers: existingLayerIds });

        if (features && features.length > 0) {
          const feature = features[0];
          // Extract element UUID from layer ID
          const layerId = feature.layer.id;
          const uuidMatch = layerId.match(/element-layer-(.+?)-(circle|line|fill|outline)/);
          if (uuidMatch) {
            const elementUuid = uuidMatch[1];
            const element = elements.find(el => el.uuid === elementUuid);
            if (element) {
              ElementItemUtil.zoomToElement(element);
              setShowElementDetailPanel(true);
              setSelectedElement(element);
            }
          }
        }
      };

      // Store handlers for cleanup
      (window.map as any).elementClickHandler = clickHandler;
      (window.map as any).elementDblClickHandler = dblClickHandler;

      // Add event listeners
      window.map?.on('click', clickHandler);
      window.map?.on('dblclick', dblClickHandler);
    }
  }, [elements, showElementLabels]);


  return (<div style={{ display: 'none' }}></div>);
};

export default memo(ElementMapController);
